<template>
  <b-card>
    <div class="d-flex align-items-center justify-content-start my-2 ml-4">
      <div
        class="px-8 py-16 cursor-pointer text-center border-2 border-dashed d-theme-border-grey-light d-theme-dark-bg text-xl"
        @click="$refs.kmlInput.click()"
        @drop="handleKmlDrop"
        @dragover="handleKmlDragover"
        @dragenter="handleKmlDragover"
      >
        <feather-icon
          icon="UploadCloudIcon"
          svg-classes="h-16 w-16 stroke-current text-grey"
          class="block"
        />
        <span>Drop KML File or </span>
        <span
          class="font-medium text-primary"
          @click.stop="$refs.kmlInput.click()"
        >Browse</span>
        <input
          ref="kmlInput"
          type="file"
          accept=".kml"
          class="d-none"
          autocomplete="off"
          @change="onKmlFileChange"
        >
      </div>
    </div>

    <b-overlay
      :show="loading"
      variant="light"
      opacity="0.30"
      blur="10px"
      rounded="sm"
      class="mt-2 mb-2"
    />

    <b-overlay
      id="overlay-background"
      variant="light"
      opacity="0.30"
      blur="10px"
      rounded="sm"
    >
      <b-table
        v-if="polygonResults.length"
        id="kml-upload-table"
        striped
        hover
        responsive
        :per-page="perPage"
        :current-page="page"
        :items="polygonResults"
        :fields="resultFields"
        show-empty
      >
        <template #empty>
          <TableDataFetching
            :rows="polygonResults"
            :data-loading="loading"
          />
        </template>
        <template #cell(polygon_id)="data">
          <div style="width: 120px;">
            {{ data.item.polygon_id || '-' }}
          </div>
        </template>
        <template #cell(polygon_name)="data">
          <div style="width: 200px;">
            {{ data.item.polygon_name || '-' }}
          </div>
        </template>
        <template #cell(status)="data">
          <div>
            <b-badge :variant="data.item.status === 'Matched' ? 'light-success' : 'light-danger'">
              {{ data.item.status === 'Matched' ? 'Matched' : 'Not Matched' }}
            </b-badge>
          </div>
        </template>
      </b-table>
      <div
        v-if="polygonResults.length"
        class="mx-2 mb-2 d-flex align-items-center justify-content-between pagination-container"
      >
        <div class="d-flex align-items-center per-page-wrapper">
          <label class="mr-3">Per page</label>
          <v-select
            v-model="perPage"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="[10, 20, 50, 100]"
            :clearable="false"
            class="per-page-selector"
          />
        </div>

        <div class="d-flex align-items-center pagination-wrapper">
          <span class="text-muted pagination-text mr-4">
            Showing {{ pageStart }} to {{ pageEnd }} of {{ polygonResults.length }} entries
          </span>

          <b-button
            v-b-tooltip.hover
            title="Previous"
            variant="link"
            size="sm"
            class="px-2 nav-btn"
            :disabled="page === 1"
            @click="page = page - 1"
          >
            <feather-icon
              icon="ChevronLeftIcon"
              size="20"
            />
          </b-button>

          <div class="d-flex align-items-center page-numbers">
            <b-button
              v-for="num in getPageNumbers()"
              :key="num"
              variant="link"
              size="sm"
              class="page-button"
              :class="{ 'active': num === page, 'ellipsis': typeof num === 'string' }"
              :disabled="typeof num === 'string'"
              @click="typeof num === 'number' && (page = num)"
            >
              {{ num }}
            </b-button>
          </div>

          <b-button
            v-b-tooltip.hover
            title="Next"
            variant="link"
            size="sm"
            class="px-2 nav-btn"
            :disabled="page === totalPages"
            @click="page = page + 1"
          >
            <feather-icon
              icon="ChevronRightIcon"
              size="20"
            />
          </b-button>
        </div>
      </div>
      <b-row
        v-if="polygonResults.length"
        class="justify-content-md-end"
      >
        <b-col>
          <b-button
            class="mr-1 float-right custom-button-color"
            :disabled="loading"
            to="/operation-settings/cities"
          >
            Done
          </b-button>
        </b-col>
      </b-row>
    </b-overlay>
  </b-card>
</template>

<script>
import {
  BCard, BTable, BButton, BRow, BCol, BOverlay, BBadge,
} from 'bootstrap-vue'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'

const KMLUploadRepository = RepositoryFactory.get('kmlFileUpload')
export default {
  components: {
    BCard, BTable, BButton, BRow, BCol, BOverlay, BBadge,
  },
  mixins: [SuccessMessage, ErrorMessage],
  data() {
    return {
      loading: false,
      kmlFile: null,
      polygonResults: [],
      resultFields: [
        { key: 'polygon_id', label: 'Polygon ID', sortable: true },
        { key: 'polygon_name', label: 'Polygon Name', sortable: true },
        { key: 'status', label: 'Matched Status', sortable: true },
      ],
      perPage: 10,
      page: 1,
    }
  },
  computed: {
    pageStart() {
      return this.polygonResults.length === 0 ? 0 : (this.page - 1) * this.perPage + 1
    },
    pageEnd() {
      const end = this.page * this.perPage
      return end > this.polygonResults.length ? this.polygonResults.length : end
    },
    totalPages() {
      return Math.ceil(this.polygonResults.length / this.perPage)
    },
  },
  methods: {
    getPageNumbers() {
      const pages = []
      const { totalPages } = this
      const current = this.page
      const delta = 2

      if (totalPages <= 1) return [1]

      pages.push(1)

      const start = Math.max(2, current - delta)
      const end = Math.min(totalPages - 1, current + delta)

      if (start > 2) {
        pages.push('...')
      }

      for (let i = start; i <= end; i += 1) {
        if (!pages.includes(i)) {
          pages.push(i)
        }
      }

      if (end < totalPages - 1) {
        pages.push('...')
      }

      if (totalPages > 1 && !pages.includes(totalPages)) {
        pages.push(totalPages)
      }

      return pages
    },
    handleKmlDrop(event) {
      event.preventDefault()
      const { files } = event.dataTransfer
      if (files && files.length) {
        [this.kmlFile] = files
        this.onKmlFileChange()
      }
    },
    handleKmlDragover(event) {
      event.preventDefault()
    },
    async onKmlFileChange(evt) {
      const file = evt?.target?.files?.[0] || this.kmlFile
      if (!file) return
      this.kmlFile = file
      this.loading = true
      try {
        const { data } = await KMLUploadRepository.uploadKml(this.kmlFile)
        this.processKmlResults(data)
        this.showSuccessMessage(data.message || 'KML file processed.')
      } catch (error) {
        this.showErrorMessage(error.response?.data?.message || 'Failed to upload KML file.')
      }
      this.loading = false
    },
    processKmlResults(data) {
      if (!data || !Array.isArray(data.data)) {
        this.polygonResults = []
        return
      }
      this.polygonResults = data.data.map(item => ({
        polygon_id: item.polygon_id || '',
        polygon_name: item.polygon_name || '',
        city_name: item.city_name || '',
        status: item.city_name ? 'Matched' : 'Not Matched',
      }))
    },
    onDone() {
      this.polygonResults = []
      this.kmlFile = null
    },
  },
}
</script>
<style lang="scss" scoped>
.repeater-form {
  overflow: hidden;
  transition: .35s height;
}
.textSmall {
    max-width: 1000px;
}
.excelUploadtable{
  padding-bottom: 250px !important;
}
.custom-button-color {
  background-color: #4186f5 !important;
  border-color: #4186f5 !important;
}
.border-error {
  border-color: red !important;
}

.pagination-container {
  padding: 1rem;
  border-top: 1px solid #e0e0e0;
  background-color: #fafafa;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
}

.per-page-wrapper {
  min-width: 180px;

  label {
    font-weight: 500;
    color: #6e6b7b;
    font-size: 0.875rem;
  }

  .per-page-selector {
    min-width: 100px;
  }
}

.pagination-wrapper {
  flex: 1;
  justify-content: flex-end;
  gap: 1rem;

  @media (max-width: 768px) {
    justify-content: flex-start;
    width: 100%;
  }
}

.pagination-text {
  font-size: 0.875rem;
  color: #6e6b7b;
  white-space: nowrap;

  @media (max-width: 768px) {
    display: none;
  }
}

.page-numbers {
  gap: 0.5rem;
  align-items: center;
}

.page-button {
  min-width: 36px;
  height: 36px;
  padding: 0 !important;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  font-size: 0.875rem;
  border-radius: 50%;
  transition: all 0.2s ease;
  color: #6e6b7b;
  border: none;

  &:hover:not(:disabled):not(.ellipsis) {
    background-color: #e8f0ff;
    color: #4186f5;
  }

  &.active {
    background-color: #4186f5;
    color: white;
    font-weight: 600;

    &:hover {
      background-color: #4186f5;
      color: white;
    }
  }

  &.ellipsis {
    cursor: default;
    color: #999;

    &:hover {
      background-color: transparent;
      color: #999;
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.nav-btn {
  padding: 0.25rem 0.5rem !important;
  color: #4186f5;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    color: #2563eb;
    transform: scale(1.1);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.per-page-selector {
  min-width: 80px;

  ::v-deep .vs__dropdown-toggle {
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 0.4rem 0.5rem;
  }

  ::v-deep .vs__selected {
    font-size: 0.875rem;
  }
}
</style>
